import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { getFunctions, httpsCallable } from "firebase/functions";
import { firebaseApp } from '../../util/firebase';
import Button from '../Button';
import TextField from '../TextField';
import LoadingIcon from '../LoadingIcon';

const functions = getFunctions(firebaseApp);

function PasswordSetupModal({ isOpen, token, onComplete, onClose }) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [canClose, setCanClose] = useState(false);

  // Escape key handler - only works when canClose is true
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && canClose && onClose) {
        onClose();
      }
    };
    if (isOpen && canClose) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, canClose, onClose]);
  const { handleSubmit, register, formState: { errors }, watch } = useForm();

  const password = watch('password');

  const onSubmit = async (data) => {
    if (!token) {
      setError('Invalid setup token. Please use the link from your email.');
      setCanClose(true);
      return;
    }

    setPending(true);
    setError('');

    try {
      // Call Cloud Function to complete password setup
      const completeSetup = httpsCallable(functions, 'completePasswordSetup');
      const result = await completeSetup({
        token: token,
        password: data.password
      });

      if (result.data.success) {
        setSuccess(true);
        setPending(false);

        // Wait 2 seconds to show success message, then complete
        setTimeout(() => {
          if (onComplete) {
            onComplete();
          }
        }, 2000);
      }
    } catch (err) {
      console.error('Password setup error:', err);

      let errorMessage = 'Failed to set password. Please try again.';

      // Handle specific error codes from Cloud Function
      if (err.code === 'functions/not-found') {
        errorMessage = 'Invalid or expired setup link.';
      } else if (err.code === 'functions/deadline-exceeded') {
        errorMessage = 'Your setup link has expired. Please check your email for a new link.';
      } else if (err.code === 'functions/failed-precondition') {
        errorMessage = 'This link has already been used.';
      } else if (err.code === 'functions/resource-exhausted') {
        errorMessage = 'Too many attempts. Please request a new link.';
      } else if (err.code === 'functions/invalid-argument') {
        errorMessage = err.message || 'Invalid password format.';
      }

      setError(errorMessage);
      setCanClose(true);
      setPending(false);
    }
  };

  // Password strength indicator
  const getPasswordStrength = (pass) => {
    if (!pass) return { strength: 0, label: '' };

    let strength = 0;
    if (pass.length >= 8) strength++;
    if (pass.length >= 12) strength++;
    if (/[a-z]/.test(pass) && /[A-Z]/.test(pass)) strength++;
    if (/\d/.test(pass)) strength++;
    if (/[^a-zA-Z\d]/.test(pass)) strength++;

    const labels = ['Weak', 'Fair', 'Good', 'Strong', 'Very Strong'];
    const colors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-green-500', 'bg-green-700'];

    return {
      strength: Math.min(strength, 5),
      label: labels[Math.min(strength - 1, 4)],
      color: colors[Math.min(strength - 1, 4)]
    };
  };

  const passwordStrength = getPasswordStrength(password);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Close button - only visible when canClose is true */}
        {canClose && onClose && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10"
            aria-label="Close"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
        <div className="p-6">
          {!success ? (
            <>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Set Your Password
              </h2>
              <p className="text-gray-600 mb-6">
                Create a secure password for your account. You'll use this to sign in next time.
              </p>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <TextField
                    type="password"
                    id="password"
                    placeholder="Password"
                    error={errors.password}
                    {...register('password', {
                      required: 'Password is required',
                      minLength: {
                        value: 8,
                        message: 'Password must be at least 8 characters'
                      },
                      validate: {
                        hasUpperCase: (v) => /[A-Z]/.test(v) || 'Must contain uppercase letter',
                        hasLowerCase: (v) => /[a-z]/.test(v) || 'Must contain lowercase letter',
                        hasNumber: (v) => /\d/.test(v) || 'Must contain a number'
                      }
                    })}
                  />

                  {/* Password strength indicator */}
                  {password && password.length > 0 && (
                    <div className="mt-2">
                      <div className="flex items-center gap-2 mb-1">
                        {[1, 2, 3, 4, 5].map((level) => (
                          <div
                            key={level}
                            className={`h-1 flex-1 rounded ${
                              level <= passwordStrength.strength
                                ? passwordStrength.color
                                : 'bg-gray-200'
                            }`}
                          />
                        ))}
                      </div>
                      {passwordStrength.label && (
                        <p className="text-xs text-gray-600">
                          Strength: {passwordStrength.label}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <TextField
                  type="password"
                  id="confirmPassword"
                  placeholder="Confirm Password"
                  error={errors.confirmPassword}
                  {...register('confirmPassword', {
                    required: 'Please confirm your password',
                    validate: (value) => {
                      if (value !== password) {
                        return 'Passwords do not match';
                      }
                      return true;
                    }
                  })}
                />

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-gray-700 font-semibold mb-1">
                    Password Requirements:
                  </p>
                  <ul className="text-xs text-gray-600 space-y-1">
                    <li className={password && password.length >= 8 ? 'text-green-700' : ''}>
                      • At least 8 characters
                    </li>
                    <li className={password && /[a-z]/.test(password) && /[A-Z]/.test(password) ? 'text-green-700' : ''}>
                      • Mix of uppercase and lowercase letters
                    </li>
                    <li className={password && /\d/.test(password) ? 'text-green-700' : ''}>
                      • At least one number
                    </li>
                  </ul>
                </div>

                <Button
                  type="submit"
                  size="md"
                  disabled={pending}
                  isBlock={true}
                >
                  {pending && <LoadingIcon className="w-6" />}
                  {!pending && 'Set Password'}
                </Button>
              </form>

              <p className="text-xs text-gray-500 mt-4 text-center">
                Keep your password safe and don't share it with anyone.
              </p>

              {/* Help link - visible when stuck (canClose is true) */}
              {canClose && (
                <p className="mt-4 text-sm text-gray-500 text-center">
                  Having trouble?{' '}
                  <a href="/support" className="text-green-600 hover:text-green-700 underline">
                    Contact support
                  </a>
                </p>
              )}
            </>
          ) : (
            // Success state
            <div className="text-center py-8">
              <div className="text-green-700 text-6xl mb-4">✓</div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Password Set Successfully!
              </h2>
              <p className="text-gray-600">
                Redirecting to your dashboard...
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PasswordSetupModal;
